<template>
    <v-card class="mt-6 overflow-hidden">
        <v-toolbar flat>
            <v-toolbar-title class="text-h4">Tableau des correspondances</v-toolbar-title>

            <v-spacer></v-spacer>

            <button type="button" class="btn btn--plain btn--primary btn-xs" @click.stop="toggleExportModal()">
                Exporter
                <v-icon dark>mdi-microsoft-excel</v-icon>
            </button>

        </v-toolbar>

        <v-divider class="m-0"></v-divider>

        <v-data-table :headers="headers" :items="filteredItems" :items-per-page="itemsPerPage" :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc" no-data-text="Aucune donnée disponible" class="elevation-1" height="500"
            fixed-header hide-default-footer hide-default-header>
            <template v-slot:header="{ props }">
                <thead>
                    <tr>
                        <template v-for="header in props.headers">
                            <th v-if="!header.hide" :key="header.text"
                                :class="['column sortable', header.value === props.options.sortBy[0] ? 'active' : '', header.value]"
                                @click="changeSort(header.value)">
                                <span class="d-flex">
                                    {{ header.text }}
                                    <v-icon v-if="props.options.sortDesc[0]" class="sort-icon">mdi-arrow-down</v-icon>
                                    <v-icon v-else class="sort-icon">mdi-arrow-up</v-icon>
                                </span>
                            </th>
                        </template>
                    </tr>
                    <tr>
                        <template v-for="header in props.headers">
                            <th v-if="!header.hide" :key="header.text">
                                <input @click.stop type="text" v-model="header.searchText"
                                    :placeholder="header.placeholder" class="search-input px-1" />
                            </th>
                        </template>
                    </tr>
                </thead>
            </template>
            <template v-slot:item="{ item }">
                <tr :class="setRowBackground(item)">
                    <MappingsTableCellValue :value="item.terminologyName" :isUserConnected="isUserConnected" />
                    <MappingsTableCellValue :value="item.type" :icon="mappingTypes[item.type].icon"
                        :isUserConnected="isUserConnected" :isMappingProtected="item.isProtected" />
                    <MappingsTableCellValue :value="item.code" :isUserConnected="isUserConnected"
                        :isMappingProtected="item.isProtected" :link="item.link" />
                    <MappingsTableCellValue :value="item.mainLabel" :isUserConnected="isUserConnected"
                        :isMappingProtected="item.isProtected" :link="item.link"
                        :linkNewtab="item.hostedBySMT ? false : true" />
                    <MappingsTableCellValue :value="item.method" :isUserConnected="isUserConnected"
                        :isMappingProtected="item.isProtected" />
                    <MappingsTableCellValue :value="item.validity" :isUserConnected="isUserConnected"
                        :isMappingProtected="item.isProtected" />
                    <MappingsTableCellValue :value="item.author" :isUserConnected="isUserConnected" />
                    <MappingsTableCellValue v-if="item.comment" @cell-clicked="toggleCommentModal(item.comment)"
                        value="Cliquer pour afficher le commentaire" icon="mdi-comment"
                        :isUserConnected="isUserConnected" :isMappingProtected="item.isProtected" />
                    <MappingsTableCellValue v-if="!item.comment" :isUserConnected="isUserConnected"
                        :isMappingProtected="item.isProtected" />
                </tr>
            </template>
            <template v-slot:footer>
                <div class="p-1 text-center">
                    <p v-if="allItemsShown" class="mb-0"> Tous les résultats sont affichés </p>
                    <button v-else type="button" class="btn btn--plain btn--primary btn-xs" @click="loadMoreItems()">
                        Afficher plus de correspondances </button>
                </div>
            </template>
        </v-data-table>
        <Modal v-if="showCommentModal" :show="showCommentModal" title="Commentaire"
            @modal-closed="toggleCommentModal()">{{
                comment }}</Modal>
        <Modal :show="showExportModal" title="Export des Alignements en CSV" @modal-closed="toggleExportModal()"
            :centered="false">
            <template v-slot:alert_message>
                <AlertMessage v-if="!isUserConnected && mappingListWithCounts.numberOfProtectedMappings > 0"
                    class="mb-3">Attention : l'export CSV de certaines correspondances nécessite d'être connecté.
                </AlertMessage>
            </template>
            <MappingExport
                :headers="Array(headers.map((el) => { return el.text }), headers.map((el) => { return el.value }))"
                :items="items" :isUserConnected="isUserConnected" @modal-closed="toggleExportModal()">
            </MappingExport>
        </Modal>
    </v-card>
</template>


<script>
import MAPPINGS_TYPES from '../../enums/mappings_types';
import Modal from '../Modal';
import MappingsTableCellValue from './MappingsTableCellValue';
import StoreConfiguration from '../../store-configuration';
import MappingExport from './MappingExport';
import TRANSLATIONS from '../../enums/translations';
import AlertMessage from '../AlertMessage';

export default {
    name: "MappingsTable",
    props: {
        mappingListWithCounts: {
            type: Object,
            default: () => { }
        },
        isUserConnected: {
            type: Boolean,
            default: false
        }
    },

    components: {
        MappingsTableCellValue,
        Modal,
        MappingExport,
        AlertMessage
    },

    data: () => {
        return {
            items: [],
            headers: [
                {
                    text: "Terminologie Cible",
                    align: "start",
                    sortable: true,
                    value: "terminologyName",
                    searchText: "",
                    isProtected: false,
                    placeholder: "Saisir une terminologie cible",
                },
                {
                    text: "ID terminologie cible",
                    value: "terminologyId",
                    hide: true
                },
                {
                    text: "Type",
                    align: "start",
                    sortable: true,
                    value: "type",
                    searchText: "",
                    isProtected: true,
                    placeholder: "Saisir un type",
                },
                {
                    text: "Code",
                    align: "start",
                    sortable: true,
                    value: "code",
                    searchText: "",
                    isProtected: true,
                    placeholder: "Saisir un code",
                },
                {
                    text: "URI concept cible",
                    value: "conceptUri",
                    hide: true
                },
                {
                    text: "Terme principal",
                    align: "start",
                    sortable: true,
                    value: "mainLabel",
                    searchText: "",
                    isProtected: true,
                    placeholder: "Saisir un terme principal",
                },
                {
                    text: "Méthode",
                    align: "start",
                    sortable: true,
                    value: "method",
                    searchText: "",
                    isProtected: true,
                    placeholder: "Saisir une méthode",
                },
                {
                    text: "Validation",
                    align: "start",
                    sortable: true,
                    value: "validity",
                    searchText: "",
                    isProtected: true,
                    placeholder: "Saisir un statut",
                },
                {
                    text: "Auteur",
                    align: "start",
                    sortable: true,
                    value: "author",
                    searchText: "",
                    isProtected: false,
                    placeholder: "Saisir un auteur",
                },
                {
                    text: "Commentaire",
                    align: "start",
                    sortable: true,
                    value: "comment",
                    searchText: "",
                    isProtected: true,
                    placeholder: "Saisir un mot",
                }
            ],
            mappingTypes: MAPPINGS_TYPES,
            showExportModal: false,
            showCommentModal: false,
            comment: "",
            itemsPerPage: 10,
            sortBy: "",
            sortDesc: false,
            methodTranslation: TRANSLATIONS.method,
            validityTranslation: TRANSLATIONS.validity
        };
    },

    mounted() {
        this.processItems();
    },

    watch: {
        $route(to, from) {
            if (to.query.lang !== from.query.lang) {
                this.processItems(to.query.lang);
            }
        }
    },

    computed: {
        filteredItems() {
            return this.items.filter((item) => {
                let sortie = true;

                for (let header of this.headers.filter((header) => header.searchText)) { //we search only if something is typed
                    //if user is connected or if the header is not protected or if the header is protected and the item is not we, display all
                    if (this.isUserConnected || !header.isProtected || header.isProtected && !item.isProtected) {
                        // if the header is type, we search in the type and in the symbol
                        if (header.value == "type") {
                            sortie = sortie && (this.textContain(item[header.value], header.searchText) || this.textContain(this.mappingTypes[item[header.value]].symbol, header.searchText));
                        }
                        else {
                            sortie = sortie && this.textContain(item[header.value], header.searchText);
                        }
                    } else if (header.isProtected && item.isProtected) {
                        sortie = false;
                    }
                }
                return sortie;
            });
        },

        allItemsShown() {
            return this.filteredItems.length <= this.itemsPerPage;
        }
    },

    methods: {
        processItems(lang = this.$route.query.lang) {
            this.items = [];
            this.mappingListWithCounts.mappings.forEach((el) => {
                this.items.push({
                    terminologyName: el.targetTerminologyTitle,
                    terminologyId: el.targetTerminologyId,
                    code: el.targetCode,
                    conceptUri: el.targetUri,
                    mainLabel: StoreConfiguration.getMultilingualValueByLangAlgorithm(el.targetPrefLabels, lang, el.targetUri),
                    method: this.getMethodTranslation(el.methods, lang),
                    validity: this.getValidityTranslation(el.validationStatus, lang),
                    type: el.type,
                    author: el.author,
                    comment: StoreConfiguration.getMultilingualValueByLangAlgorithm(el.comments, lang),
                    isProtected: el.protected,
                    link: StoreConfiguration.getConceptTargetUrl(el.targetUri, el.targetTerminologyId, el.hostedBySMT, this.$searchResultManager.conceptsCatalogSlug, this.$route.query)
                });
            });
        },

        setRowBackground(mapping) {
            if (!this.isUserConnected && mapping.isProtected) {
                return "";
            }
            if (mapping.validity == TRANSLATIONS.validity.VALIDATED.en || mapping.validity == TRANSLATIONS.validity.VALIDATED.fr) {
                return "mapping-validate"
            } else if (mapping.validity == TRANSLATIONS.validity.PROPOSED.en || mapping.validity == TRANSLATIONS.validity.PROPOSED.fr) {
                return "mapping-to-validate"
            }
            return "";
        },

        toggleCommentModal(comment = "") {
            this.comment = comment;
            this.showCommentModal = !this.showCommentModal;
        },

        toggleExportModal() {
            this.showExportModal = !this.showExportModal;
        },

        textContain(itemValue, searchText) {
            return searchText ? (itemValue ? itemValue.toLowerCase().includes(searchText.toLowerCase()) : false) : true;
        },

        loadMoreItems() {
            this.itemsPerPage += 10;
        },

        changeSort(header) {
            if (this.sortBy === header) {
                if (this.sortDesc) {
                    this.sortBy = ''; // disable the sort
                    this.sortDesc = false;
                }
                else {
                    this.sortDesc = true;
                }
            }
            else {
                this.sortBy = header;
            }
        },

        getMethodTranslation(methods, lang) {
            let sortie = "";
            if (!methods) {
                return sortie;
            }
            methods.forEach(method => {
                if (sortie && !sortie.endsWith(", ")) {
                    sortie += ", ";
                }
                if (this.methodTranslation[method]) {
                    sortie += this.methodTranslation[method][lang] || this.methodTranslation[method]["en"]
                }
                else {
                    sortie += method;
                }
            });

            if (sortie.trim().endsWith(',')) {
                sortie = sortie.trim().slice(0, -1);
            }

            return sortie
        },

        getValidityTranslation(validity, lang) {
            if (validity && this.validityTranslation[validity]) {
                return this.validityTranslation[validity][lang] || this.validityTranslation[validity]["en"]
            }
            return validity;
        }
    }
}
</script>

<style lang="scss" scoped>
.v-data-table::v-deep .v-data-table__wrapper>table {

    table-layout: fixed;

    >tbody>tr>td,
    >thead>tr>th {
        vertical-align: middle;
        width: 100%;
    }

    th.type {
        width: 40%;
    }

    th.mainLabel {
        width: 300%;
    }

    th.code {
        width: 80%;
    }

    >tbody>tr>td {
        font-size: 1.5rem;
    }

    >tbody {
        border-top: thin solid #0000001f;
    }

    >thead>tr>th {
        font-size: 1.8rem;
    }

    >thead>tr:last-child>th {
        border-bottom: 0;
    }
}

.theme--light.v-data-table>.v-data-table__wrapper>table>thead>tr>th {
    padding: 0px 10px 0px;
    cursor: pointer;
    box-shadow: none;

    .search-input {
        width: 100%;
        border: thin solid #0000001f;
        font-size: 1.5rem;
        font-weight: normal;
        cursor: auto;
    }

    .sort-icon {
        opacity: 0;
    }

    &.active,
    &:hover {
        color: rgba(0, 0, 0, .87);

        .sort-icon {
            opacity: 1;
            color: rgba(0, 0, 0, .87);
        }
    }
}
</style>